import * as THREE from 'three';
import { adoptGlow } from '../materials.ts';
import { animTexture } from './atlas.ts';

/**
 * Rigid GPU skinning from the animation atlas. Every vertex of a rigged model belongs to one bone (`aBone`);
 * each instance carries what it plays in two attributes:
 *   aAnimA = (first texel of clip A, its frames, frame position, cross-fade weight of B)
 *   aAnimB = (first texel of clip B, its frames, frame position, bones of the rig)
 * The shader reads the bone's two samples around the frame position (4 texels), and 4 more only while a
 * cross-fade runs, then rotates, scales and moves the vertex and turns its normal.
 */
const DECL = /* glsl */ `
attribute float aBone;
attribute vec4 aAnimA;
attribute vec4 aAnimB;
uniform highp sampler2D uAnim;
vec4 animQ;
vec4 animT;
vec4 animTexel(float i) {
  int k = int(i + 0.5);
  int w = textureSize(uAnim, 0).x;
  return texelFetch(uAnim, ivec2(k - (k / w) * w, k / w), 0);
}
void animSample(float base, float frames, float f, float bones, out vec4 q, out vec4 t) {
  float f0 = floor(f);
  float k = f - f0;
  float f1 = min(f0 + 1.0, frames - 1.0);
  float i0 = base + (f0 * bones + aBone) * 2.0;
  float i1 = base + (f1 * bones + aBone) * 2.0;
  q = normalize(mix(animTexel(i0), animTexel(i1), k));
  t = mix(animTexel(i0 + 1.0), animTexel(i1 + 1.0), k);
}
void animEval() {
  float bones = aAnimB.w;
  animSample(aAnimA.x, aAnimA.y, aAnimA.z, bones, animQ, animT);
  if (aAnimA.w > 0.001) {
    vec4 q2; vec4 t2;
    animSample(aAnimB.x, aAnimB.y, aAnimB.z, bones, q2, t2);
    q2 *= sign(dot(animQ, q2) + 1e-6);
    animQ = normalize(mix(animQ, q2, aAnimA.w));
    animT = mix(animT, t2, aAnimA.w);
  }
}
vec3 animRot(vec3 v) { return v + 2.0 * cross(animQ.xyz, cross(animQ.xyz, v) + animQ.w * v); }
`;

export const patchSkin = (shader: THREE.WebGLProgramParametersWithUniforms): void => {
  shader.uniforms.uAnim = animTexture;
  shader.vertexShader = shader.vertexShader
    .replace('#include <common>', `#include <common>\n${DECL}`)
    .replace('void main() {', 'void main() {\n  animEval();')
    .replace('#include <beginnormal_vertex>', '#include <beginnormal_vertex>\n  objectNormal = animRot(objectNormal);')
    .replace('#include <begin_vertex>', '#include <begin_vertex>\n  transformed = animRot(transformed * animT.w) + animT.xyz;');
};

const VARIANTS = new WeakMap<THREE.Material, THREE.Material>();

/** The skinned twin of a material (same look, same program cache family, plus the bone transform). */
export const skinned = (m: THREE.Material): THREE.Material => {
  let v = VARIANTS.get(m);
  if (v) return v;
  const c = m.clone();
  const prev = m.onBeforeCompile;
  const prevKey = m.customProgramCacheKey();
  c.onBeforeCompile = (shader, renderer): void => {
    prev.call(m, shader, renderer);
    patchSkin(shader);
  };
  c.customProgramCacheKey = (): string => `${prevKey}|somnhold-skin`;
  c.name = `${m.name}+skin`;
  // Translucent and double-sided (a ghost's cloak): three draws it in two passes and flips `side` with a
  // needsUpdate on every object, every frame, which re-keys its program each time (over 1000 times a second
  // with a crowd on screen, a phone's whole CPU). One pass looks the same on these miniatures.
  c.forceSinglePass = true;
  adoptGlow(c);
  v = c;
  VARIANTS.set(m, v);
  return v;
};
